import { useEffect, useMemo, useRef, useState } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { resolveStreamUrl } from "../player/streamToken.js";
import { admissionMessage, dvr, type DvrAdmission, type DvrProgrammeRequest } from "./dvr.js";

interface RecordDialogProps {
  program: Program;
  channel: Channel;
  onClose: () => void;
}

const KEEP_OPTIONS = [0, 3, 5, 10] as const;

/**
 * Record from the guide (Section 20, Android app only): this programme, or the
 * whole series. The space check runs before anything is booked, so the viewer
 * sees whether it fits — and why not — instead of finding a cut-off recording.
 */
export function RecordDialog({ program, channel, onClose }: RecordDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ open: true, onClose, rootRef: dialogRef });
  const [mode, setMode] = useState<"once" | "series">("once");
  const [anyChannel, setAnyChannel] = useState(false);
  const [newOnly, setNewOnly] = useState(true);
  const [keep, setKeep] = useState<number>(0);
  const [preview, setPreview] = useState<DvrAdmission | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const startMs = new Date(program.start).getTime();
  const endMs = new Date(program.end).getTime();
  const airing = useMemo(() => formatAiring(startMs, endMs), [startMs, endMs]);

  async function request(): Promise<DvrProgrammeRequest> {
    const { url } = await resolveStreamUrl(channel.streamUrl);
    return {
      title: program.title,
      ...(program.subtitle ? { subtitle: program.subtitle } : {}),
      ...(program.description ? { description: program.description } : {}),
      channelId: channel.id,
      channelName: channel.name,
      url,
      startMs,
      endMs,
    };
  }

  useEffect(() => {
    let cancelled = false;
    void request()
      .then((req) => dvr.preview(req))
      .then((answer) => {
        if (!cancelled) setPreview(answer);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // The programme and channel are fixed for the dialog's life.
  }, []);

  async function confirm() {
    setBusy(true);
    try {
      if (mode === "once") {
        const answer = await dvr.schedule(await request());
        setResult(answer.ok
          ? { ok: true, text: `Recording booked: ${program.title}, ${airing}.` }
          : { ok: false, text: admissionMessage(answer) });
      } else {
        const rule = await dvr.addRule({
          title: program.title,
          ...(anyChannel ? {} : { channelId: channel.id, channelName: channel.name }),
          preferChannelId: channel.id,
          newOnly,
          keep,
        });
        // Book the airing the viewer is looking at straight away; the guide check
        // finds the rest (it can take a minute on a large guide).
        const answer = await dvr.schedule({ ...(await request()), ruleId: rule.id });
        setResult({
          ok: true,
          text: answer.ok
            ? `Series recording set up for ${program.title}. This airing is booked; upcoming episodes will appear in Recordings shortly.`
            : `Series recording set up for ${program.title}, but this airing couldn't be booked: ${admissionMessage(answer)}`,
        });
      }
    } catch {
      setResult({ ok: false, text: "Couldn't set up the recording. Try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="epg-program-detail-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="epg-program-detail record-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Record ${program.title}`}
        tabIndex={-1}
      >
        <div className="epg-detail-header">
          <span className="epg-detail-channel">{channel.number ? `${channel.number} ` : ""}{channel.name}</span>
        </div>
        <h2 className="epg-detail-title"><span className="rec-dot" aria-hidden="true" /> Record {program.title}</h2>
        <p className="epg-detail-subtitle">{airing}{program.subtitle ? ` · ${program.subtitle}` : ""}</p>

        {result ? (
          <>
            <p className={result.ok ? "record-dialog__ok" : "record-dialog__error"} role="status">{result.text}</p>
            <div className="epg-detail-actions">
              <button className="epg-detail-play" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div className="record-dialog__choice" role="radiogroup" aria-label="What to record">
              <button role="radio" aria-checked={mode === "once"} className={mode === "once" ? "is-active" : ""} onClick={() => setMode("once")}>
                This programme
              </button>
              <button role="radio" aria-checked={mode === "series"} className={mode === "series" ? "is-active" : ""} onClick={() => setMode("series")}>
                Whole series
              </button>
            </div>

            {mode === "series" && (
              <div className="record-dialog__options">
                <div className="record-dialog__choice" role="radiogroup" aria-label="Which channel">
                  <button role="radio" aria-checked={!anyChannel} className={!anyChannel ? "is-active" : ""} onClick={() => setAnyChannel(false)}>
                    On {channel.name}
                  </button>
                  <button role="radio" aria-checked={anyChannel} className={anyChannel ? "is-active" : ""} onClick={() => setAnyChannel(true)}>
                    On any channel
                  </button>
                </div>
                <div className="record-dialog__choice" role="radiogroup" aria-label="Which episodes">
                  <button role="radio" aria-checked={newOnly} className={newOnly ? "is-active" : ""} onClick={() => setNewOnly(true)}>
                    New episodes
                  </button>
                  <button role="radio" aria-checked={!newOnly} className={!newOnly ? "is-active" : ""} onClick={() => setNewOnly(false)}>
                    Every airing
                  </button>
                </div>
                {newOnly && (
                  <p className="record-dialog__hint">
                    If the guide doesn't say which episode an airing is, one airing a day is recorded, so repeats on other feeds are skipped.
                  </p>
                )}
                <div className="record-dialog__choice" role="radiogroup" aria-label="How many to keep">
                  {KEEP_OPTIONS.map((n) => (
                    <button key={n} role="radio" aria-checked={keep === n} className={keep === n ? "is-active" : ""} onClick={() => setKeep(n)}>
                      {n === 0 ? "Keep all" : `Keep latest ${n}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className={preview && !preview.ok ? "record-dialog__error" : "record-dialog__space"}>
              {preview ? admissionMessage(preview) : "Checking space…"}
            </p>

            <ul className="record-dialog__notes">
              <li>
                The device can be in <strong>standby</strong> (turned off with the remote) and it will wake up to record.
                If it's <strong>fully powered off or unplugged</strong>, the recording won't happen.
              </li>
              <li>A stick powered from the TV's USB port loses power when the TV turns off — use its wall adapter.</li>
              <li>Recording uses one of your provider's streams; watching another channel at the same time may not work if your plan allows only one.</li>
            </ul>

            <div className="epg-detail-actions">
              <button className="epg-detail-play" onClick={() => void confirm()} disabled={busy || (mode === "once" && preview?.ok === false)}>
                {busy ? "Booking…" : <><span className="rec-dot" aria-hidden="true" /> {mode === "once" ? "Record" : "Record series"}</>}
              </button>
              <button className="epg-detail-close" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatAiring(startMs: number, endMs: number): string {
  const start = new Date(startMs);
  const day = start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const time = (d: Date) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${day}, ${time(start)} – ${time(new Date(endMs))}`;
}
